import { defineStore } from "pinia"
import { ref } from 'vue'
import { useSuiteStore } from "./useSuiteStore"
import { withApiState } from '@/utils/withApiState'

export const useOrderDerogationStore = defineStore('order-derogation', () => {
  const suiteStore = useSuiteStore()

  const isLoadingDerogations = ref(false)
  const derogations = ref<any[]>([])
  const pendingCounts = ref<number|null>(null)
  const total = ref(0)
  const totalPages = ref(1)
  const currentDerogation = ref<any | null>(null)
  const statusCounts = ref<Record<string, number>>({
    PENDING: 0,
    APPROVED: 0,
    REJECTED: 0,
  })

  const fetchCheckDerogation = async (orderUuid: string) => {
    try {
      const response = await suiteStore.suiteFetch(`order-derogation/check/${orderUuid}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (response && response.ok) {
        currentDerogation.value = await response.json()
      }
    } catch (error) {
      console.error('Erreur lors de la vérification de la dérogation:', error)
      return false
    }
  }

  const clearCurrentDerogation = () => {
    currentDerogation.value = null
  }

  const fetchDerogation = async (derogationUuid: string) => {
    try {
      const response = await suiteStore.suiteFetch(`order-derogation/${derogationUuid}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        return await response.json()
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de la dérogation:', error)
    }
    return null
  }

  const fetchMyDerogations = async (options: {
    page?: number;
    pageSize?: number;
    status?: string;
    orderBy?: string;
    order?: string;
    search?: string;
    authorUuid?: string;
    createdAt?: any;
  } = {}) => {
    isLoadingDerogations.value = true
    try {
      // Construire le payload avec tous les filtres
      const payload: any = {
        page: options.page,
        pageSize: options.pageSize,
        status: options.status,
        orderBy: options.orderBy,
        order: options.order,
      }

      // Ajouter les filtres additionnels s'ils sont présents
      if (options.search) {
        payload.search = options.search
      }

      if (options.authorUuid) {
        payload.requesterUuid = options.authorUuid
      }

      // Mapper le filtre de date depuis le format YYYY-MM vers le format backend
      if (options.createdAt) {
        payload.createdAt = options.createdAt
      }

      const response = await suiteStore.suiteFetch('order-derogation/me', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      })

      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        derogations.value = Array.isArray(data.data) ? data.data : []
        pendingCounts.value = typeof data.totalPending === 'number' ? data.totalPending : null
        total.value = typeof data.total === 'number' ? data.total : derogations.value.length
        totalPages.value = typeof data.totalPages === 'number' ? data.totalPages : Math.max(1, Math.ceil(total.value / (options.pageSize ?? 20)))
        if (data.statusCounts) {
          statusCounts.value = data.statusCounts
        }
        return data
      }

      if (response && typeof response !== 'boolean') {
        const errorData = await response.json().catch(() => ({}))
        console.error('Erreur API dérogations:', errorData.message || `HTTP ${response.status}`)
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des dérogations:', error)
    } finally {
      isLoadingDerogations.value = false
    }
    derogations.value = []
    pendingCounts.value = null
    statusCounts.value = {
      PENDING: 0,
      APPROVED: 0,
      REJECTED: 0,
    }
    return null
  }

  const fetchSubordinatesDerogations = async (options: {
    page?: number;
    pageSize?: number;
    status?: string;
    orderBy?: string;
    order?: string;
    search?: string;
    createdAt?: any;
    includeOwnDerogations?: boolean;
  } = {}) => {
    isLoadingDerogations.value = true
    try {
      // Construire le payload avec tous les filtres
      const payload: any = {
        page: options.page,
        pageSize: options.pageSize,
        status: options.status,
        orderBy: options.orderBy,
        order: options.order,
      }

      // Ajouter les filtres additionnels s'ils sont présents
      if (options.search) {
        payload.search = options.search
      }

      // Mapper le filtre de date depuis le format YYYY-MM vers le format backend
      if (options.createdAt) {
        payload.createdAt = options.createdAt
      }

      if (options.includeOwnDerogations !== undefined) {
        payload.includeOwnDerogations = options.includeOwnDerogations
      }

      const response = await suiteStore.suiteFetch('order-derogation/my-subordinates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      })

      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        derogations.value = Array.isArray(data.data) ? data.data : []
        pendingCounts.value = typeof data.totalPending === 'number' ? data.totalPending : null
        total.value = typeof data.total === 'number' ? data.total : derogations.value.length
        totalPages.value = typeof data.totalPages === 'number' ? data.totalPages : Math.max(1, Math.ceil(total.value / (options.pageSize ?? 20)))
        if (data.statusCounts) {
          statusCounts.value = data.statusCounts
        }
        return data
      }

      if (response && typeof response !== 'boolean') {
        const errorData = await response.json().catch(() => ({}))
        console.error('Erreur API dérogations:', errorData.message || `HTTP ${response.status}`)
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des dérogations des subordonnés:', error)
    } finally {
      isLoadingDerogations.value = false
    }
    derogations.value = []
    pendingCounts.value = null
    statusCounts.value = {
      PENDING: 0,
      APPROVED: 0,
      REJECTED: 0,
    }
    return null
  }

  const addDerogationComment = async (derogationUuid: string, text: string) => {
    const response = await suiteStore.suiteFetch(`order-derogation/${derogationUuid}/comment`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
      credentials: 'include',
    })

    if (response && typeof response !== 'boolean' && response.ok) {
      const data = await response.json()
      const index = derogations.value.findIndex(d => d.uuid === derogationUuid)
      if (index !== -1) {
        derogations.value[index] = { ...derogations.value[index], comment: data.comment }
      }
      return data
    }

    if (response && typeof response !== 'boolean') {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `HTTP ${response.status}`)
    }
  }

  const updateDerogationStatus = async (derogationUuid: string, status: 'APPROVED' | 'REJECTED', comment?: string) => {
    return withApiState(
      async () => {
        const payload: any = { decision: status }
        if (comment) {
          payload.decisionComment = comment
        }

        const response = await suiteStore.suiteFetch(`order-derogation/${derogationUuid}/decide`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'include',
        })

        if (response && typeof response !== 'boolean' && response.ok) {
          const data = await response.json()
          const index = derogations.value.findIndex(d => d.uuid === derogationUuid)
          if (index !== -1) {
            derogations.value[index] = data
          }
          return data
        }

        if (response && typeof response !== 'boolean') {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || `HTTP ${response.status}`)
        }
      },
      { logLabel: 'Erreur lors de la mise à jour du statut de la dérogation:', rethrow: true },
    )
  }

  return {
    isLoadingDerogations,
    derogations,
    pendingCounts,
    total,
    totalPages,
    currentDerogation,
    statusCounts,
    clearCurrentDerogation,
    fetchDerogation,
    fetchCheckDerogation,
    fetchMyDerogations,
    fetchSubordinatesDerogations,
    addDerogationComment,
    updateDerogationStatus,
  }
})
